/**
 * Diese Komponente bindet dann das jeweilige Modul ein, lädt die Seiten und übergibt den User, sowie die verfügbaren Socialmediakanäle und die jeweiligen Pfade und Token.
 */

import 'regenerator-runtime/runtime'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  Box,
  Divider,
  Grid,
  LinearProgress,
  linearProgressClasses,
  LinearProgressProps,
  Paper,
  Stack,
  Theme,
  Typography
} from '@mui/material'

import { ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'

import { getTheme, getUser, namedRequestStatus } from '../../../../store/common/common.selectors'
import {
  getDeleteRequestFetching,
  getNetworkContentPage,
  getTemplates
} from '../../../../store/dashboard/dashboard.selector'

import {
  DeferredPost,
  DeferredPostStatus,
  PostTemplate,
  PostTemplateSort,
  PostTemplatesSearchResult,
  PublishedPostWithPostContent,
  SortingOrder,
} from '@3m5/sm_publish-frontend-api'

import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import Pagination from '../../../../shared/components/NavigationComponents/Pagination'
import ImportProfessocialPost from '../../../../shared/components/ImportComponents/ImportProfessocialPost'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import { FindPostTemplatesForCurrentUserLightweightRequest } from '@3m5/sm_publish-frontend-api'
import {
  deletePostTemplateInitiated,
  findPostTemplatesForCurrentUserInitiated,
  setNetworkContentPage,
} from '../../../../store/dashboard/dashboard.actions'
import Statistic from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/Statistic'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import Trash from '@professocial/professocialPosting/src/shared/icons/Trash'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DeletePostTemplateRequest } from '@3m5/sm_publish-frontend-api'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { useHistory, useLocation } from 'react-router-dom'
import { useRouteMatch } from 'react-router'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { CoreRootState } from '../../../../store/coreRoot.reducer'
import { NetworkType } from '@professocialApi/professocial-core-frontend-api'
import { PublishedPost } from '@3m5/sm_publish-frontend-api'
import DashboardNetworkContentPostDetailsTable from '../components/DashboardNetworkContentPostDetailsTable'

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
}))

export const LinearProgressWithLabel = (props: LinearProgressProps & {
  value: number,
  total: number,
  title: string
}) => (
  <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
      <Box>
        <Typography variant="body1" color="text.secondary">{props.title}</Typography>
      </Box>
      <Box>
        <Typography variant="body1" color="grey.500">{`${props.value} von ${props.total}`}</Typography>
      </Box>
    </Box>
    <Box sx={{ width: '100%', mr: 1 }}>
      <StyledLinearProgress variant="determinate" {...props} />
    </Box>
  </>
)

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: '10px',
  },
  headline: {
    textTransform: 'uppercase',
    margin: `${theme.spacing(2)} 0`,
  },
  root: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'inherit',
    paddingTop: '9px',
    width: '100%',
  },
  icon: {
    verticalAlign: 'top',
    fill: theme.palette.info.main,
  },
}))

const EditorDashboardNetworkContent: React.FC = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { url } = useRouteMatch()
  const location = useLocation<{ detail?: any }>()
  const history = useHistory()
  const refs = useRef<HTMLDivElement[]>([])

  const user: any = useSelector(getUser)
  const theme: Theme = useSelector(getTheme)
  const deleteRequestFetching = useSelector(getDeleteRequestFetching)

  const dispatchGetPostTemplates = useCallback((params: FindPostTemplatesForCurrentUserLightweightRequest) => dispatch(findPostTemplatesForCurrentUserInitiated(params)), [dispatch])
  const dispatchDeletePostTemplate = useCallback((params: DeletePostTemplateRequest) => dispatch(deletePostTemplateInitiated(params)), [dispatch])
  const dispatchSetPage = useCallback((params: number) => dispatch(setNetworkContentPage(params)), [dispatch])

  const templateData: PostTemplatesSearchResult | undefined = useSelector(getTemplates)
  const page: number = useSelector(getNetworkContentPage)
  const statusOnGetPostTemplates: string | undefined = useSelector((state: CoreRootState) => namedRequestStatus(state, findPostTemplatesForCurrentUserInitiated.type))

  const pageSize = 10
  // eslint-disable-next-line no-unused-vars
  const [data, setData] = useState<PostTemplatesSearchResult>()
  const [toast, setToast] = React.useState<any>()

  const loadData = () => {
    const params: FindPostTemplatesForCurrentUserLightweightRequest = {
      order: SortingOrder.Desc,
      sort: PostTemplateSort.ByPostStart,
      postStartTo: new Date(),
      limit: pageSize,
      subscribable: true,
      offset: pageSize * (page - 1),
    }
    dispatchGetPostTemplates(params)
  }

  useEffect(() => {
    if (deleteRequestFetching) {
      setTimeout(loadData, 1000)
    }
  }, [deleteRequestFetching])

  const sendDeleteRequest = (deletePublished: boolean, templateId: string) => {
    setToast(null)
    dispatchDeletePostTemplate({ templateId, deletePublished })
  }

  const deletePostTemplateQuestion = (templateId: string) => {
    setToast({
      autoClose: false,
      open: true,
      type: 'error',
      title: t('networkContentMessages:deleteWarningTitle'),
      message: t('coreCalendar:deleteTemplateWarning'),
      severity: 'error',
      requestActions: true,
      confirmLabel: t('coreCalendar:templateAndPosts'),
      cancelLabel: t('coreCalendar:onlyTemplate'),
      handleClose: () => setToast(null),
      handleCancel: () => sendDeleteRequest(false, templateId),
      handleConfirm: () => sendDeleteRequest(true, templateId),
    })
  }

  useEffect(() => {
    templateData && setData(templateData)

    if (location?.state?.detail && templateData) {
      const index = templateData?.templates.findIndex((data) => data.id === location.state.detail.templateId)

      index !== -1 && refs?.current[index].id === templateData.templates[index].id ? setTimeout(() => {
        const yOffset = -135
        const y = refs.current[index].getBoundingClientRect().top + window.scrollY + yOffset

        window.scrollTo({ top: y, behavior: 'smooth' })
        history.replace({ state: {} })
      }, 500) : history.replace({ state: {} })
    }
  }, [templateData])

  useEffect(() => {
    if (page) {
      loadData()
      refs.current[0] && window.scrollTo({ top: refs.current[0].getBoundingClientRect().top, behavior: 'smooth' })
    }
  }, [page])

  /**
   * Determines whether to show the details button based on the provided PublishedPostWithPostContent object.
   * Only show the button when there are any meaningful statistics to display
   * @param {PublishedPostWithPostContent} ppwc - The PublishedPostWithPostContent object.
   * @returns {boolean} - A boolean value indicating whether to show the details button.
   */
  const shouldShowDetailsButton = useCallback((ppwc?: PublishedPostWithPostContent, template?: PostTemplate) => {
    return ([
      template?.manualSuccess,
      template?.automaticSuccess,
      template?.manualFail,
      template?.automaticFail
    ].reduce((prev, currentValue) => {
      return (prev ?? 0) + (currentValue ?? 0) || 0
    }) || 0) > 0
  }, [user, templateData, templateData?.templates])

  const countDeferredPostsAwaitingUserConfirmation = (postTemplate: PostTemplate): number => {
    const filter = templateData?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status === DeferredPostStatus.AwaitingUserConfirmation
    )
    return filter?.length || 0
  }

  const countOtherDeferredPosts = (postTemplate: PostTemplate): number => {

    const filter = templateData?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status !== DeferredPostStatus.TokenInvalid &&
        deferredPost.status !== DeferredPostStatus.ErrorWhilePosting &&
        deferredPost.status !== DeferredPostStatus.AwaitingUserConfirmation &&
        deferredPost.status !== DeferredPostStatus.NoActiveUsersForPage
    )
    return filter?.length || 0
  }

  const countNoActiveUsersForPagePosts = (postTemplate: PostTemplate): number => {
    const filter = templateData?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status === DeferredPostStatus.NoActiveUsersForPage
    )
    return filter?.length || 0
  }

  return (
    <Grid
      container
      direction="row"
    >
      <Grid container item xs={12}>
        <Grid item xs={12}>
          <Typography className={classes.headline}
            variant="h3">{t('coreDashboard:postingOverview')}</Typography>
        </Grid>
        <Grid item xs={12}>
          {user && templateData && templateData.templates.length > 0
            ? templateData.templates.map((postTemplate: PostTemplate, index: number) => {
              const publishedPost: PublishedPost | undefined = templateData.publishedPosts.find((elem: PublishedPost) => elem.templateId === postTemplate.id)
              const deferredPost: DeferredPost | undefined = templateData.deferredPosts.find((elem: DeferredPost) => elem.templateId === postTemplate.id)
              const publishPostWithPostContent: PublishedPostWithPostContent | undefined = publishedPost && {
                publishedPost: publishedPost,
                postContent: postTemplate.postContent,
              }

              return (
                <React.Fragment key={index}>
                  {postTemplate &&
                    <Paper
                      className={classes.paper}
                      ref={(ref) => {
                        if (ref) {
                          refs.current.push(ref)
                        }}}
                      id={postTemplate.id}>
                      <Grid container sx={{ position: 'relative' }}>
                        <ImportProfessocialPost
                          postConfiguration={{
                            theme: theme,
                            userRole: user.role,
                            size: ScreenEnum.xs,
                            view: ViewEnum.template,
                            post: postTemplate,
                            publishedPost: publishPostWithPostContent,
                            deferredPost: deferredPost,
                          }}
                        />
                        <Grid container item spacing={2}>
                          <Grid item xs={12}><Divider/></Grid>
                          <Grid
                            container
                            item
                            xs={12}
                            md={6}
                            paddingTop={0}
                            sx={{ paddingTop: 0 }}
                          >
                            <DashboardNetworkContentPostDetailsTable
                              type='general'
                              title={t('coreCalendar:autoPublications')}
                              pending={countOtherDeferredPosts(postTemplate)}
                              successful={postTemplate.automaticSuccess}
                              failure={postTemplate.automaticFail}
                            />
                          </Grid>
                          <Grid
                            container
                            item
                            xs={12} md={6}
                            alignItems="flex-end"
                            justifyContent="flex-end"
                            paddingTop={0}
                          >
                            <DashboardNetworkContentPostDetailsTable
                              type='awaiting'
                              title={t('coreCalendar:individualPublications')}
                              pending={countDeferredPostsAwaitingUserConfirmation(postTemplate)}
                              successful={postTemplate.manualSuccess}
                              failure={postTemplate.manualFail}
                            />
                          </Grid>
                          <Grid item xs={12}><Divider/></Grid>
                          <Grid item xs={12}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                              alignItems="" justifyContent="space-between">
                              <Box display="flex">
                                <Statistic
                                  networkPageId=""
                                  post={postTemplate}
                                  view={ViewEnum.single}
                                  justifyContent="start"
                                  networkType={NetworkType.Facebook}
                                  value={{
                                    postViews: postTemplate.views,
                                    postImpressions: postTemplate.impressionsTotal,
                                    postClicks: postTemplate.clicks,
                                    postReactionsLikeTotal: postTemplate.likes,
                                    shares: postTemplate.shares,
                                    commentCount: postTemplate.comments,
                                    postEngagedUsers: postTemplate.engagementRate * 100,
                                    postEngagedFan: postTemplate.relativeReach * 100,
                                  }}
                                />
                              </Box>
                              <Stack direction="row" spacing={3}>
                                {
                                  shouldShowDetailsButton(publishPostWithPostContent, postTemplate)
                                    ? (
                                      <Box>
                                        <ButtonComponent
                                          text={
                                            <>
                                              <span style={{ paddingRight: '6px' }}>
                                                <InfoOutlinedIcon
                                                  className={classes.icon}
                                                  width="23px"/>
                                              </span>{t('networkContentDashboard:postDetails')}
                                            </>
                                          }
                                          onClick={() => history.push({ pathname: `${url}/templateDetails/${postTemplate.id}` })}
                                          className={classes.root}
                                          variant="text"
                                          color="info"
                                          disableElevation
                                        />
                                      </Box>
                                    )
                                    : null
                                }
                                {/* <Box> */}
                                {/*  <ButtonComponent */}
                                {/*    text={ */}
                                {/*      <> */}
                                {/*        <span style={{ paddingRight: '6px' }}> */}
                                {/*          <ReplyAllOutlinedIcon className={classes.icon} /> */}
                                {/*        </span>{t('networkContentDashboard:postAgain')} */}
                                {/*      </> */}
                                {/*    } */}
                                {/*    className={classes.root} */}
                                {/*    variant='text' */}
                                {/*    color='info' */}
                                {/*  // onClick={() => setShowDeletePostWarning(true)} */}
                                {/*  /> */}
                                {/* </Box> */}
                                <Box>
                                  <ButtonComponent
                                    text={
                                      <>
                                        <span style={{ paddingRight: '2px' }}>
                                          <Trash className={classes.icon} width="23px"/>
                                        </span>{t('networkContentDashboard:deletePost')}
                                      </>
                                    }
                                    className={classes.root}
                                    variant="text"
                                    color="info"
                                    onClick={() => deletePostTemplateQuestion(postTemplate.id)}
                                  />
                                </Box>
                              </Stack>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>}
                </React.Fragment>
              )
            })
            : statusOnGetPostTemplates === 'success' ?
              <NoDataComponent noDataText={t('networkContentMessages:noPostsYet')} alignment="center"
                margin="16px 0"/> : <Preloader/>
          }
        </Grid>
        <Grid item xs={12}>
          <Box component="div" style={{ display: 'flex', justifyContent: 'center' }}>
            {templateData &&
              <Pagination
                totalItems={templateData.total}
                itemsPerPage={pageSize}
                page={page}
                setPage={(page: number) => dispatchSetPage(page)}
              />}
          </Box>
        </Grid>
      </Grid>
      {toast &&
        <DialogComponent
          type={toast.type || undefined}
          autoClose={toast.autoClose}
          title={toast.title}
          message={toast.message}
          open={toast.open}
          confirmLabel={toast.confirmLabel}
          cancelLabel={toast.cancelLabel}
          requestActions={toast.requestActions}
          onClose={toast.handleClose}
          onCancel={toast.handleCancel}
          onConfirm={toast.handleConfirm}
        />}
    </Grid>
  )
}
export default EditorDashboardNetworkContent
